import { faPencilAlt as solidPencilAlt } from "@jewlr/pro-solid-svg-icons/faPencilAlt"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Image,
  Typography,
} from "@jewlr/storybook/core"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import styled from "styled-components"

import {
  updateChoice,
  setExtraWizardParams,
  setPhotoBoxViewIndex,
} from "areas/product/store/actions"
import { openModal } from "components/modal"
import { jewlr_t } from "helpers/application"
import StorageHelper from "helpers/local-storage-cache"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { useNavigation } from "helpers/use-navigation"

const BoxName = styled(Typography)`
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
`

const BoxPrice = styled(Typography)`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 10px;
`

const BoxDesc = styled(Typography)`
  color: ${(props) => props.theme.colors.grey[65]};
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
`

const ImageContainer = styled(Box).attrs((props) => ({
  alignSelf: "center",
  ...props,
}))`
  aspect-ratio: 452 / 294;
  ${(props) => props.theme.mediaQueries.tablet`
    height: 110px;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    height: 134px;
  `}
`

const StyledImage = styled(Image)`
  height: 100%;
  object-fit: contain;
`

const BoxOption = styled(Button).attrs({
  variant: "text",
})`
  background-color: ${(props) => props.theme.colors.white};
  border: 2px solid ${(props) => props.theme.colors.grey["10"]};
  border-radius: 12px;
  border-radius: 12px;
  display: block;
  margin: 0 0 20px 0;
  padding: 10px;
  text-align: left;
  width: 100%;
  &:focus {
    border: 2px solid ${(props) => props.theme.colors.primary};
    outline: none;
  }
  ${(props) => props.theme.mediaQueries.tablet`
    border-radius: 0;
    margin: 0 0 5px 0;
    padding: 12px;
    border: 3px solid transparent;
    transitiion: none;
  `}
  transition: border-color 0.3s ease-in-out;
  &:after {
    clear: both;
    content: "";
    display: table;
  }
  ${(props) =>
    props.selected
      ? `
      position: relative;
      border-color: ${props.theme.colors.selected};
      box-shadow: 2px 4px 10px 0px rgba(0,0,0,0.4);
      &:before {
        display: block;
        position: absolute;
        content: url(${require("images/icons/checkmark.svg")});
        transform: scale(0.05);
        bottom: -2px;
        right: -2px;
        z-index: 2;
        width: 22px;
        height: 22px;
      }
      ${props.theme.mediaQueries.tablet`
        border: 1px solid #dddddd;
        &:before {
          display: none;
        }
      `}
    `
      : ""}
`

const PhotoButtons = styled(Button).attrs({
  forwardedAs: "span",
})`
  border-radius: 5px;
  font-size: 18px;
  font-weight: 300;
  margin-top: 10px;
  padding: 18px;
  width: 100%;
`

const ModifyButton = styled(Button).attrs((props) => ({
  color: "link",
  fontSize: "12px",
  forwardedAs: "span",
  m: "8px 0 0",
  padding: "0",
  variant: "text",
  ...props,
}))``

const PackagingBox = ({
  boxImage,
  choice,
  display,
  isMobile,
  modify,
  selectBox,
  selected,
  opt,
}) => {
  const keyboardProps = useNavigation()
  const selectedOpt = selected == opt.selected_type
  return (
    <BoxOption
      aria-selected={selectedOpt}
      data-cy={`product-box-design-type-${opt.selected_type}`}
      data-name={opt.name}
      data-price={to_currency(opt.price)}
      onClick={(e) => {
        selectBox(e, opt)
      }}
      role="radio"
      selected={selectedOpt}
      tabIndex={selectedOpt ? 0 : -1}
      {...keyboardProps}
    >
      <Flex data-pref="box">
        <Box flex="1 0 52%" p={{ _: "0", tablet: "10px" }}>
          <BoxName>{opt.title}</BoxName>
          <BoxPrice>
            {opt.strikethrough_price ? (
              <>
                <strike>{to_currency(opt.strikethrough_price)}</strike>{" "}
                <Box as="span" color="sale" display="inline-block">
                  {to_currency(opt.price)} ON SALE
                </Box>
              </>
            ) : (
              `${to_currency(opt.price)}`
            )}
          </BoxPrice>
          <BoxDesc>{opt.description}</BoxDesc>
          {opt.customizable && !isMobile ? (
            <ModifyButton
              data-pref="box"
              display={{
                _: "none",
                tablet: !choice[`${opt.selected_type}_value`]
                  ? "none"
                  : "block",
              }}
              onClick={(e) => {
                modify(opt.selected_type, e)
              }}
            >
              <FontAwesomeIcon icon={solidPencilAlt} /> Modify
            </ModifyButton>
          ) : null}
        </Box>
        <ImageContainer>
          <StyledImage
            alt={opt.image_alt}
            data-pref="box"
            ratio={
              opt.selected_type === "premium" ? "auto 452 / 294" : "auto 1 / 1"
            }
            src={
              opt.selected_type === "photo"
                ? boxImage || imageLookup("box", opt.image, "normal")
                : display[`${opt.selected_type}_image`] ||
                  imageLookup("box", opt.image, "normal")
            }
          />
        </ImageContainer>
      </Flex>
      {opt.selected_type === "photo" && isMobile ? (
        choice["photo_value"] ? (
          <PhotoButtons
            data-pref="box"
            onClick={(e) => {
              modify(opt.selected_type, e, isMobile)
            }}
            variant="primary"
          >
            Modify
          </PhotoButtons>
        ) : (
          <PhotoButtons
            data-cy={`product-box-design-type-${opt.title}-btn-mobile`}
            variant="primary"
          >
            <Image
              alt="Upload"
              mr={1}
              ratio="auto 512 / 512"
              src={require("images/icons/upload-arrow.svg")}
              width="13px"
            />
            {" Upload"}
          </PhotoButtons>
        )
      ) : null}
      {opt.selected_type === "design" && isMobile ? (
        choice["design_value"] ? (
          <PhotoButtons
            onClick={(e) => {
              modify(opt.selected_type, e, isMobile)
            }}
            variant="primary"
          >
            Modify
          </PhotoButtons>
        ) : (
          <PhotoButtons variant="primary">Choose a design</PhotoButtons>
        )
      ) : null}
    </BoxOption>
  )
}
PackagingBox.propTypes = {
  boxImage: PropTypes.string,
  choice: PropTypes.object,
  display: PropTypes.object,
  isMobile: PropTypes.bool,
  modify: PropTypes.func,
  opt: PropTypes.object,
  selectBox: PropTypes.func,
  selected: PropTypes.string,
}

const InternalBoxOptions = ({
  choice,
  cropper,
  data,
  display,
  isMobile,
  modify,
  selectBox,
  selected,
  skipCache,
}) => {
  if (!data || !choice) {
    return null
  }

  var cacheKey = `cropper-box-${cropper.aspect_ratio_width}-${cropper.aspect_ratio_height}`
  var boxImage = null
  if (
    StorageHelper.exist(cacheKey) &&
    display &&
    display.photo_image &&
    !skipCache
  ) {
    boxImage = StorageHelper.get(cacheKey)
    if (boxImage) {
      if (display.photo_image) {
        var found = display.photo_image.match(/src={(.*?)}/)
        if (found && found.length) {
          /**
           * If cached cropped version of image exist, replace existing image url with cached url
           */
          if (cropper.use_imgsmbly) {
            boxImage = display.photo_image
              .replace(/src={(.*?)}/, "cachedString")
              .replace(/cachedString/, `src={${boxImage.path}}`)
          } else {
            boxImage = display.photo_image
              .replace(/src={(.*?)}/, "cachedString")
              .replace(
                /cachedString/,
                `src={${boxImage.path.replace(/https/, "http")}}`
              )
          }
        } else {
          // In case of non-Adobe Scene7 preview image URL, return it directly
          boxImage = display.photo_image
        }
      }
    }
  } else {
    boxImage = display.photo_image
  }
  return (
    <div>
      <FocusScope restoreFocus>
        <Box
          aria-label={`${data.heading.title} Options`}
          data-hashid="box"
          data-label={`${jewlr_t("terms.Personalize")} Your Ring Box`}
          data-pref="box"
          data-summary-field={`${jewlr_t("terms.Personalize")} Your Ring Box`}
          m="0 0 5px 0"
          p={{ _: "10px 20px", tablet: "10px 0" }}
          pr={{ tablet: 2 }}
          role="radiogroup"
        >
          {data.options.map((opt) => (
            <PackagingBox
              boxImage={boxImage}
              choice={choice}
              display={display}
              isMobile={isMobile}
              key={`Box-${opt.title}`}
              modify={modify}
              opt={opt}
              selectBox={selectBox}
              selected={selected}
            />
          ))}
        </Box>
      </FocusScope>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  var data = state.wizard.data.box
  let display = state.display.box || {}
  let choice = state.choices.box
  let selected = choice ? choice.selected_type || "default" : "default"
  let experimentVariation = state.wizard.experimentVariation
  let isMobile = state.browser.is.mobile
  let style_code = state.choices.style_code?.value || null
  let cropper = state.wizard.data.box?.cropper || null

  return {
    choice,
    cropper,
    data,
    display,
    experimentVariation,
    image:
      state && state.choices && state.choices[ownProps.pref]
        ? state.choices[ownProps.pref].cropper_image
        : null,
    isMobile,
    photoBoxViewIndex: state.halfModal.photoBoxViewIndex,
    selected,
    skipCache: !!state.router.location?.query?.line_item_id,
    style_code,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch,
    setPhotoBoxViewIndex: (index) => {
      dispatch(setPhotoBoxViewIndex(index))
    },
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { choice } = stateProps
  const { dispatch } = dispatchProps

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    modify: (type) => {
      if (type == "photo") {
        openModal("#photo-upload")
      } else if (type == "design") {
        openModal("#box-design")
      }
    },
    selectBox: (event, opt) => {
      if (opt.selected_type == "photo") {
        if (!choice[`${opt.selected_type}_value`]) {
          openModal("#photo-upload")
        }
        dispatch(setExtraWizardParams({ nobox: false }))
      } else if (opt.selected_type == "design") {
        openModal("#box-design")
        dispatch(setExtraWizardParams({ nobox: false }))
      }
      if (
        opt.selected_type != "design" ||
        (opt.selected_type == "design" && choice["design_value"])
      ) {
        dispatch(updateChoice("box", opt, true))
      }
    },
  }
}

InternalBoxOptions.propTypes = {
  choice: PropTypes.object,
  cropper: PropTypes.object,
  data: PropTypes.object,
  display: PropTypes.object,
  isMobile: PropTypes.bool,
  modify: PropTypes.func,
  selectBox: PropTypes.func,
  selected: PropTypes.string,
  skipCache: PropTypes.bool,
}

const BoxOptions = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalBoxOptions)

export default BoxOptions
